// Slug helpers for /writing/:url.
//
// Keep slugify() in lockstep with slugify() in src/scripts/upload_md_file.py — the
// publish script mints a post's slug with the Python version, and the site resolves it
// with this one. slugParity.test.js pins the two together.
//
// Why this exists: App.js drives the router with history@5, which hands
// `window.location.pathname` to the route *undecoded*. A browser percent-encodes any
// non-ASCII character in a pathname, so a stored slug containing `ü` never matched the
// `%C3%BC` the route param carried and the post rendered blank. New slugs are ASCII-only
// and URL-safe by construction; lookup is tolerant so every already-published URL keeps
// working in both raw and percent-encoded form.

// Letters whose NFKD decomposition carries no combining mark, so stripping marks alone
// would drop them entirely.
const ASCII_FOLD = {
  'ß': 'ss', 'æ': 'ae', 'Æ': 'ae', 'œ': 'oe', 'Œ': 'oe',
  'ø': 'o', 'Ø': 'o', 'đ': 'd', 'Đ': 'd', 'ð': 'd', 'Ð': 'd',
  'ł': 'l', 'Ł': 'l', 'þ': 'th', 'Þ': 'th', 'ı': 'i',
};

function foldToAscii(text) {
  return String(text)
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^ -]/g, (ch) => ASCII_FOLD[ch] || ASCII_FOLD[ch.normalize('NFKD')] || '');
}

// Canonical slug for a title: lowercase ASCII, alphanumerics kept, every other run of
// characters collapsed to a single hyphen, no leading/trailing hyphens.
export function slugify(title) {
  return foldToAscii(title)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// Decode a route param defensively: a malformed escape (`%E0%A4%A`) must not throw.
function safeDecode(param) {
  const s = String(param);
  try {
    return decodeURIComponent(s);
  } catch (e) {
    try {
      return decodeURI(s);
    } catch (e2) {
      return s;
    }
  }
}

// Comparison key for a slug or route param: decode, then normalize the same way slugify
// does, so `money,-science,-and-power`, `money%2C-science%2C-and-power` and
// `money-science-and-power` all collapse to one key.
export function normalizeSlug(param) {
  return slugify(safeDecode(param));
}

// Resolve a route param to a post. Exact match first (the common case), then by
// normalized key against each post's stored url, then against the slug its title would
// generate today. Returns undefined when nothing matches.
export function findPostBySlug(posts, param) {
  if (param == null) return undefined;
  const exact = posts.find((p) => p.url === param);
  if (exact) return exact;
  const key = normalizeSlug(param);
  if (!key) return undefined;
  return (
    posts.find((p) => normalizeSlug(p.url) === key) ||
    posts.find((p) => slugify(p.title) === key)
  );
}
